/**
 * Comparative Analysis Section for Accountant Report View
 * Displays industry benchmarks, similar claims, and data quality indicators
 */

import React from 'react';
import CommentsSection from '../CommentsSection';
import { formatMoneyOrNotComputed } from '../../utils/financialFormat';

interface ComparativeAnalysis {
  status?: string;
  client_loss?: number;
  event_type?: string;
  comparisons?: {
    sample_size?: number;
    similar_claims_average?: number;
    similar_claims_median?: number;
  };
  percentile?: {
    value?: number;
    interpretation?: string;
  };
  insights?: any[];
  comparable_claims?: Array<{
    business_type: string;
    event_type: string;
    loss_amount: number;
  }>;
  loss_to_revenue_ratio?: number;
  business_revenue_estimate?: number;
  data_quality?: {
    reliability?: 'HIGH' | 'MEDIUM' | 'LOW';
    sample_size?: number;
  };
  error?: string;
}

interface ComparativeAnalysisSectionProps {
  reportId: string;
  businessType?: string;
  comparativeAnalysis: ComparativeAnalysis | null;
  loadingAnalysis: boolean;
  canComment: boolean;
  getAuthHeader: () => { Authorization: string } | {};
  formatCurrency: (amount?: number) => string;
}

export default function ComparativeAnalysisSection({
  reportId,
  businessType,
  comparativeAnalysis,
  loadingAnalysis,
  canComment,
  getAuthHeader,
  formatCurrency
}: ComparativeAnalysisSectionProps) {
  return (
    <div className="rounded-md border p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <h2 className="cs-h2 mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
        Comparative Analysis - Industry Context
      </h2>
      <p className="text-sm mb-6" style={{ color: 'var(--color-text-muted)' }}>
        This section compares the claim against industry benchmarks and similar events to validate reasonableness. 
        Critical for establishing credibility in litigation and demonstrating the claim is not an outlier.
      </p>
      
      {loadingAnalysis ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-brand)' }}></div>
          <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>Loading comparative analysis...</p>
        </div>
      ) : comparativeAnalysis && comparativeAnalysis.status === 'success' ? (
        <>
          {/* Claim vs Industry Benchmarks */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="rounded-md p-4 border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>THIS CLAIM</p>
              <p className="text-sm mb-2 font-semibold" style={{ color: 'var(--color-verified)' }}>Final Amount</p>
              <p className="text-3xl font-bold mb-2 font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>
                {formatCurrency(comparativeAnalysis.client_loss)}
              </p>
              <div className="text-xs rounded px-2 py-1" style={{ color: 'var(--color-verified)', backgroundColor: 'transparent' }}>
                {businessType || 'Business'} - {comparativeAnalysis.event_type}
              </div>
            </div>

            <div className="rounded-md p-4 border-l-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>SIMILAR CLAIMS AVERAGE</p>
              <p className="text-sm mb-2 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
                {comparativeAnalysis.comparisons?.sample_size || 0} Similar Cases
              </p>
              {comparativeAnalysis.comparisons?.similar_claims_average == null ? (
                <p className="text-base font-medium mb-2 font-mono" style={{ color: 'var(--color-text-ghost)' }}>
                  {formatMoneyOrNotComputed(null)}
                </p>
              ) : (
                <p className="text-3xl font-bold mb-2 font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                  {formatMoneyOrNotComputed(comparativeAnalysis.comparisons.similar_claims_average)}
                </p>
              )}
              <div className="text-xs rounded px-2 py-1" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-hover)' }}>
                Median: {formatMoneyOrNotComputed(comparativeAnalysis.comparisons?.similar_claims_median)}
              </div>
            </div>

            <div className="rounded-md p-4 border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>PERCENTILE RANK</p>
              <p className="text-sm mb-2 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Comparison</p>
              <p className="text-3xl font-bold mb-2 font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                {comparativeAnalysis.percentile?.value?.toFixed(0) || 0}th
              </p>
              <div className="text-xs rounded px-2 py-1" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                {comparativeAnalysis.percentile?.interpretation || 'Average'}
              </div>
            </div>
          </div>

          {/* Insights from AI */}
          {comparativeAnalysis.insights && comparativeAnalysis.insights.length > 0 && (
            <div className="border rounded-md p-4 mb-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <h3 className="cs-h3 mb-3 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                Key Insights
              </h3>
              <ul className="space-y-2">
                {comparativeAnalysis.insights.map((insight: any, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    <span className="mt-0.5" style={{ color: 'var(--color-text-muted)' }}>•</span>
                    <span>
                      {typeof insight === 'string' ? insight : insight.message || insight.title || JSON.stringify(insight)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Comparable Claims */}
          {comparativeAnalysis.comparable_claims && comparativeAnalysis.comparable_claims.length > 0 && (
            <div className="border rounded-md p-4 mb-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <h3 className="cs-h3 mb-3 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                Similar Cases in Database ({comparativeAnalysis.comparable_claims.length})
              </h3>
              <div className="space-y-2">
                {comparativeAnalysis.comparable_claims.map((claim, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm rounded-md px-3 py-2" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                    <span style={{ color: 'var(--color-text-secondary)' }}>
                      {claim.business_type} - {claim.event_type}
                    </span>
                    <span className="font-semibold font-mono tabular-nums text-right" style={{ color: 'var(--color-needs-human-text)' }}>
                      {formatCurrency(claim.loss_amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loss to Revenue Ratio */}
          <div className="border rounded-md p-4 mb-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
            <h3 className="cs-h3 mb-2" style={{ color: 'var(--color-text-primary)' }}>Loss to Revenue Ratio</h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              This claim represents <span className="font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>{comparativeAnalysis.loss_to_revenue_ratio?.toFixed(1)}%</span> of
              estimated annual revenue ({formatMoneyOrNotComputed(comparativeAnalysis.business_revenue_estimate)})
            </p>
          </div>

          {/* Data Quality Indicator */}
          {comparativeAnalysis.data_quality && (
            <div className="border rounded-md p-4" style={{ backgroundColor: comparativeAnalysis.data_quality.reliability === 'HIGH' ? 'transparent' : 'var(--color-bg-elevated)', borderColor: comparativeAnalysis.data_quality.reliability === 'HIGH' ? 'var(--color-verified)' : 'var(--color-border)' }}>
              <div className="flex items-start gap-3">
                {comparativeAnalysis.data_quality.reliability === 'HIGH' ? (
                  // HIGH = verified ground state: hueless check glyph.
                  <svg className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-verified)' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  // Non-HIGH must NOT wear the verified check (mis-signals trust) — warning triangle, orange.
                  <svg className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                )}
                <div className="flex-1">
                  <p className="text-sm font-semibold mb-1" style={{ color: comparativeAnalysis.data_quality.reliability === 'HIGH' ? 'var(--color-verified)' : 'var(--color-text-primary)' }}>
                    {comparativeAnalysis.data_quality.reliability} Reliability Analysis
                  </p>
                  <p className="text-sm" style={{ color: comparativeAnalysis.data_quality.reliability === 'HIGH' ? 'var(--color-verified)' : 'var(--color-text-secondary)' }}>
                    Based on {comparativeAnalysis.data_quality.sample_size} similar claims in your database. 
                    {comparativeAnalysis.data_quality.reliability === 'HIGH' && ' Strong statistical confidence in benchmarks.'}
                    {comparativeAnalysis.data_quality.reliability === 'MEDIUM' && ' Moderate statistical confidence. Consider adding more comparative data.'}
                    {comparativeAnalysis.data_quality.reliability === 'LOW' && ' Limited comparative data. Benchmarks should be used with caution.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="border rounded-md p-4 text-center" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {comparativeAnalysis?.error ? `Analysis error: ${comparativeAnalysis.error}` : 'No comparative analysis data available'}
          </p>
        </div>
      )}
      
      {/* Comments on Comparative Analysis */}
      {canComment && (
        <CommentsSection
          reportId={reportId}
          section="comparative_analysis"
          sectionTitle="Comparative Analysis"
          getAuthHeader={getAuthHeader}
          canComment={true}
        />
      )}
    </div>
  );
}
